import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, formatDistanceToNowStrict, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import { DATE_TIME_FORMAT } from '@/lib/constants'

// ─────────────────────────────────────────────────────────────────────────────
// cn — class name helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * cn — concat + merge inteligente de classes Tailwind.
 *
 * - `clsx` aceita strings, arrays, objetos condicionais.
 * - `tailwind-merge` resolve conflitos entre classes Tailwind (ex.: `p-2 p-4` → `p-4`).
 *
 * Usado por todos os componentes Shadcn/ui e também em código de produto.
 *
 * Exemplo:
 *   cn('p-2', condition && 'p-4', { 'text-red-500': isError })
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

// ─────────────────────────────────────────────────────────────────────────────
// Datas
// ─────────────────────────────────────────────────────────────────────────────

/** Aceita Date, ISO string ou timestamp numérico e devolve um Date válido (ou null). */
function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (typeof value === 'number') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  // String: tentar parseISO primeiro; fallback para Date constructor.
  const parsed = parseISO(value)
  if (!Number.isNaN(parsed.getTime())) return parsed
  const fallback = new Date(value)
  return Number.isNaN(fallback.getTime()) ? null : fallback
}

/**
 * Formata uma data ISO 8601 (ou Date) para o padrão brasileiro `dd/MM/yyyy HH:mm`.
 *
 * - Locale: pt-BR (date-fns).
 * - Entradas inválidas retornam `'-'` (placeholder neutro para UI).
 *
 * @param value     ISO string, Date ou timestamp em ms.
 * @param formatStr Override opcional do formato (default: DATE_TIME_FORMAT).
 *
 * Exemplo:
 *   formatDate('2024-01-15T14:30:00Z')          → '15/01/2024 11:30' (depende do TZ)
 *   formatDate(new Date(), 'dd/MM/yyyy')        → '15/01/2024'
 */
export function formatDate(
  value: Date | string | number | null | undefined,
  formatStr: string = DATE_TIME_FORMAT,
): string {
  const date = toDate(value)
  if (!date) return '-'
  return format(date, formatStr, { locale: ptBR })
}

/**
 * Converte uma duração em segundos para string compacta `Xm Ys`.
 *
 * - Frações são truncadas (`Math.floor`) — alinhado com timers de execução
 *   do Orchestrator, que reportam duração em segundos inteiros para a UI.
 * - 0 (ou valor inválido) retorna `'0s'`.
 * - Para durações ≥ 1h adiciona o prefixo de horas: `Xh Ym Ys`.
 *
 * Exemplos:
 *   formatDuration(125.7) → '2m 5s'
 *   formatDuration(45)    → '45s'
 *   formatDuration(0)     → '0s'
 *   formatDuration(3725)  → '1h 2m 5s'
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) {
    return '0s'
  }

  const total = Math.floor(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`
  }
  return `${secs}s`
}

/**
 * Retorna tempo relativo em pt-BR no estilo `há X min/h/d`.
 *
 * - Usa `formatDistanceToNowStrict` do date-fns (sem palavras "aproximadamente").
 * - Casos limites:
 *     • < 30s              → 'há poucos segundos'
 *     • inválido / vazio   → '-'
 * - Datas no futuro também usam o prefixo `há` (a granularidade é abstraída);
 *   se precisar diferenciar futuro/passado, use diretamente o date-fns.
 *
 * Saídas típicas (locale pt-BR + addSuffix):
 *   • 'há 3 minutos'
 *   • 'há 2 horas'
 *   • 'há 5 dias'
 *
 * Exemplos:
 *   formatRelativeTime(new Date())                                 → 'há poucos segundos'
 *   formatRelativeTime(new Date(Date.now() - 3 * 60 * 1000))       → 'há 3 minutos'
 */
export function formatRelativeTime(
  value: Date | string | number | null | undefined,
): string {
  const date = toDate(value)
  if (!date) return '-'

  const diffMs = Math.abs(Date.now() - date.getTime())
  if (diffMs < 30_000) {
    return 'há poucos segundos'
  }

  return formatDistanceToNowStrict(date, { addSuffix: true, locale: ptBR })
}
